import { LodPointCloud, LodTreeNode } from "@faro-lotv/lotv";
import { useEffect } from "react";
import { Box3, Vector3, Vector3Tuple } from "three";
import { sampleLodPoints } from "../point-sampling/sample-lod-points";
import { computeDataRegion, RegionBox } from "./smart-clipping";

/** A coarse sample of the whole data, enough for the box at a fraction of a second */
const SAMPLE_POINTS = 50_000;
/** Waits for the user to stop changing the selection, e.g. checking several scans */
const SETTLE_DELAY_MS = 400;

/**
 * @param root The root of the LOD tree
 * @param point A position in the frame of the tree
 * @param depth The level to measure the density at, its nodes are known once the level above is downloaded
 * @returns the points of the node of that level containing the point, 0 if the tree has no data there
 */
function nodeDensity(root: LodTreeNode, point: Vector3, depth: number): number {
  let node = root;
  while (node.depth < depth) {
    const child = node.children.find(({ boundingBox }) =>
      boundingBox.containsPoint(point),
    );
    if (!child) return 0;
    node = child;
  }
  return node.numPoints;
}

type UseSmartClippingProps = {
  /** The point cloud */
  pointCloud: LodPointCloud;
  /** Indices of the scans left out of the selection, as used by the scanIds attribute of the points */
  hiddenScanIndices: ReadonlySet<number>;
  /** World positions of the static scanners of the data, to find floors hidden from above */
  scannerPositions: Vector3Tuple[];
  /** Used if the data has too few points */
  fallbackBox: Box3;
  /** True to compute the region */
  isActive: boolean;
  /** Called with the computed region */
  onComputed(region: RegionBox): void;
};

/**
 * Computes a region box around the selected data, turned to follow the walls of the buildings, while isActive is true.
 * The box comes from a coarse sample of the whole data, so data out of view is kept too.
 */
export function useSmartClipping({
  pointCloud,
  hiddenScanIndices,
  scannerPositions,
  fallbackBox,
  isActive,
  onComputed,
}: UseSmartClippingProps): void {
  useEffect(() => {
    if (!isActive) return;
    const abort = new AbortController();
    const timeout = setTimeout(() => {
      sampleLodPoints(pointCloud, {
        maxPoints: SAMPLE_POINTS,
        hiddenScanIndices,
        signal: abort.signal,
      })
        .then(({ positions, origin, depth }) => {
          const toTree = pointCloud.matrixWorld.clone().invert();
          const inTree = new Vector3();
          const points: Vector3Tuple[] = [];
          const densities: number[] = [];
          for (let i = 0; i < positions.length; i += 3) {
            const point: Vector3Tuple = [
              positions[i] + origin.x,
              positions[i + 1] + origin.y,
              positions[i + 2] + origin.z,
            ];
            points.push(point);
            inTree.fromArray(point).applyMatrix4(toTree);
            densities.push(
              nodeDensity(pointCloud.tree.root, inTree, depth + 1),
            );
          }
          return computeDataRegion(points, scannerPositions, densities);
        })
        // A failed download leaves the fallback box, the user can still edit it
        .catch(() => undefined)
        .then((computed) => {
          if (abort.signal.aborted) return;
          onComputed(computed ?? { box: fallbackBox.clone(), yaw: 0 });
        });
    }, SETTLE_DELAY_MS);
    return () => {
      clearTimeout(timeout);
      abort.abort();
    };
  }, [
    fallbackBox,
    hiddenScanIndices,
    isActive,
    onComputed,
    pointCloud,
    scannerPositions,
  ]);
}
